import React, { useState, useEffect } from 'react';
import { walletApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Wallet,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Zap,
  FileText,
  MessageSquare,
  Phone,
  Plus,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { toast } from 'sonner';

const ACTIVITY_ICONS = {
  message_generation: MessageSquare,
  call_analysis: Phone,
  proposal_generation: FileText,
  follow_up: Zap,
  scheduling: Zap
};

const ACTIVITY_COLORS = {
  message_generation: '#3B82F6',
  call_analysis: '#8B5CF6',
  proposal_generation: '#F59E0B',
  follow_up: '#10B981',
  scheduling: '#6366F1'
};

export default function WalletPage() {
  const { user, refreshUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('100');
  const [topUpLoading, setTopUpLoading] = useState(false);

  useEffect(() => {
    loadWalletData();
  }, []);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      const [summaryRes, transactionsRes, pricingRes] = await Promise.all([
        walletApi.getUsageSummary(),
        walletApi.getTransactions({ limit: 20 }),
        walletApi.getPricing()
      ]);
      setSummary(summaryRes.data);
      setTransactions(transactionsRes.data);
      setPricing(pricingRes.data);
    } catch (error) {
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setTopUpLoading(true);
    try {
      await walletApi.topUp(amount);
      toast.success(`Successfully added ${amount} credits`);
      setShowTopUpModal(false);
      setTopUpAmount('100');
      loadWalletData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to top up');
    } finally {
      setTopUpLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-40 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const usageData = pricing ? Object.entries(pricing)
    .filter(([key]) => key !== 'currency' && key !== 'note')
    .map(([key, value]) => ({
      name: key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      cost: value,
      color: ACTIVITY_COLORS[key] || '#94A3B8'
    })) : [];

  return (
    <div className="p-8" data-testid="wallet-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">AI Wallet</h1>
          <p className="text-slate-500 mt-1">Manage your AI credits and usage</p>
        </div>
        <Button
          className="btn-primary gap-2"
          onClick={() => setShowTopUpModal(true)}
          data-testid="topup-btn"
        >
          <Plus className="w-4 h-4" />
          Top Up Credits
        </Button>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="metric-card bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Wallet className="w-8 h-8 opacity-80" />
              {summary?.low_balance_warning && (
                <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded-full">
                  Low Balance
                </span>
              )}
            </div>
            <p className="text-blue-100 text-sm">Available Credits</p>
            <p className="text-4xl font-bold tabular-nums mt-1">
              {summary?.current_balance?.toFixed(0) || 0}
            </p>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <TrendingDown className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-slate-500 text-sm">Used This Month</p>
            <p className="text-3xl font-bold tabular-nums mt-1">
              {summary?.monthly_usage?.toFixed(0) || 0}
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Week: {summary?.weekly_usage?.toFixed(0) || 0} credits
            </p>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <p className="text-slate-500 text-sm">Total Topped Up</p>
            <p className="text-3xl font-bold tabular-nums mt-1">
              {summary?.total_topped_up?.toFixed(0) || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pricing */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-600" />
              AI Action Pricing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {usageData.map((item, i) => {
                const Icon = ACTIVITY_ICONS[item.name.toLowerCase().replace(/ /g, '_')] || Zap;
                return (
                  <div 
                    key={i}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${item.color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: item.color }} />
                      </div>
                      <span className="text-sm font-medium">{item.name}</span>
                    </div>
                    <span className="font-semibold">{item.cost} credits</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-4">
              {pricing?.note}
            </p>
          </CardContent>
        </Card>

        {/* Transactions */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No transactions yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {transactions.map((tx, i) => (
                  <div 
                    key={i}
                    className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        tx.transaction_type === 'credit' 
                          ? 'bg-emerald-100' 
                          : 'bg-red-100'
                      }`}>
                        {tx.transaction_type === 'credit' ? (
                          <ArrowUpRight className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{tx.description}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(tx.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${
                        tx.transaction_type === 'credit' 
                          ? 'text-emerald-600' 
                          : 'text-red-600'
                      }`}>
                        {tx.transaction_type === 'credit' ? '+' : ''}{tx.amount.toFixed(0)}
                      </p>
                      <p className="text-xs text-slate-400">
                        Balance: {tx.balance_after.toFixed(0)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Up Modal */}
      <Dialog open={showTopUpModal} onOpenChange={setShowTopUpModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Top Up Credits</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Amount (credits)</Label>
            <Input
              type="number"
              value={topUpAmount}
              onChange={(e) => setTopUpAmount(e.target.value)}
              min="1"
              max="10000"
              className="mt-2"
              data-testid="topup-amount-input"
            />
            
            {/* Quick amounts */}
            <div className="flex gap-2 mt-4">
              {[50, 100, 250, 500, 1000].map(amount => (
                <Button
                  key={amount}
                  variant="outline"
                  size="sm"
                  onClick={() => setTopUpAmount(amount.toString())}
                  className={topUpAmount === amount.toString() ? 'border-blue-600 bg-blue-50' : ''}
                >
                  {amount}
                </Button>
              ))}
            </div>

            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Credits</span>
                <span>{topUpAmount || 0}</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-slate-500">Estimated cost</span>
                <span>${((parseFloat(topUpAmount) || 0) * 0.01).toFixed(2)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTopUpModal(false)}>
              Cancel
            </Button>
            <Button
              className="btn-primary"
              onClick={handleTopUp}
              disabled={topUpLoading}
              data-testid="confirm-topup-btn"
            >
              {topUpLoading ? 'Processing...' : 'Add Credits'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
